import type { ReactNode, Ref } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

export interface SegmentedOption<V extends string | number> {
  value: V;
  label: ReactNode;
}

export interface SegmentedControlProps<V extends string | number> {
  /** Currently selected value. */
  value: V;
  /** 2–4 options. */
  options: SegmentedOption<V>[];
  /** Fired with the newly selected value (never deselects). */
  onChange: (value: V) => void;
  size?: 'small' | 'medium';
  /** Accessible group label (translated copy). */
  ariaLabel?: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Pill toggle for exclusive choices (carrier filter, scope toggle, role view).
 * Built on MUI `ToggleButtonGroup`; ignores null selections so one option is
 * always active.
 */
export function SegmentedControl<V extends string | number>({
  value,
  options,
  onChange,
  size = 'small',
  ariaLabel,
  ref,
}: SegmentedControlProps<V>) {
  return (
    <ToggleButtonGroup
      ref={ref}
      exclusive
      size={size}
      value={value}
      aria-label={ariaLabel}
      onChange={(_, next: V | null) => {
        if (next !== null) onChange(next);
      }}
      sx={{
        bgcolor: 'background.default',
        p: 0.25,
        borderRadius: '6px',
        '& .MuiToggleButton-root': {
          border: 0,
          textTransform: 'none',
          fontSize: 13,
          color: 'text.secondary',
        },
        '& .MuiToggleButton-root.Mui-selected': {
          bgcolor: 'background.paper',
          boxShadow: '0 1px 2px rgba(4,29,51,0.08)',
          fontWeight: 600,
          color: 'text.primary',
        },
        '& .MuiToggleButton-root.Mui-selected:hover': {
          bgcolor: 'background.paper',
        },
      }}
    >
      {options.map((option) => (
        <ToggleButton key={String(option.value)} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
